import { computed } from 'vue';
import { useCreateStore } from '@/stores/create.ts';
import { SigneeType } from '@/types/enums/SigneeType.ts';
import type { Signee } from '@/types/Signee.ts';

/**
 * When a quorum (requiredSignatures / requiredApprovals) may be configured,
 * for both editing surfaces of the participants step.
 *
 * `OrderedInvitees` asks per sign-order group; `UnorderedInvitees` asks about
 * the single bucket every invitee sits in while the sign order is off. The
 * rule has to live in one place: it is also mirrored on the persistence side
 * by `resetUnsupportedGroupQuorums` in the create store, which clears a quorum
 * the rule no longer allows.
 */
export function useQuorumEligibility() {
  const createStore = useCreateStore();

  // Quorum is an envelope-only feature: the frontend treats a document as an
  // envelope when it carries files.
  const isEnvelope = computed(
    () => (createStore.document?.files?.length ?? 0) > 0
  );

  // Whether there's a real sequence of 2+ non-empty groups (as opposed to just
  // one group, which needs its own, size-based rule — see
  // `canShowGroupQuorum`).
  const hasMultipleGroups = computed(
    () =>
      createStore.signeesOrdered.filter(
        (group: Array<Signee>) => group.length > 0
      ).length >= 2
  );

  // The single bucket holding every invitee while the sign order is off:
  // `createUnorderedGroup` collapses them into one group with signOrder 0.
  const ungroupedParticipants = computed<Array<Signee>>(
    () => createStore.signeesOrdered[0] ?? []
  );

  function hasApprover(group: Array<Signee>): boolean {
    return group.some((signee) => signee.signeeType === SigneeType.APPROVE);
  }

  function hasSignee(group: Array<Signee>): boolean {
    return group.some((signee) => signee.signeeType === SigneeType.SIGN);
  }

  // True when a group mixes approvers and signees.
  function isMixedGroup(group: Array<Signee>): boolean {
    return hasApprover(group) && hasSignee(group);
  }

  // Sequenced view: a quorum can only ever apply to a group of 2+ members, and
  // either a real sequence of 2+ groups, or — when there's only a single
  // (non-empty) group in play — that single group having 2+ members. Mixed
  // groups meet these size/sequence conditions too, but never support a quorum
  // — the footer then shows an explanatory message instead (see
  // `isMixedGroup`).
  function canShowGroupQuorum(group: Array<Signee>): boolean {
    if (!group || group.length <= 1 || !isEnvelope.value) return false;
    if (hasMultipleGroups.value) return true;
    return group.length > 1;
  }

  // Ungrouped view: the same 2+ members rule, applied to the one bucket every
  // invitee shares. An all-approver bucket is excluded outright rather than
  // shown with a message — unlike a sequenced all-approver group (which
  // legitimately carries requiredApprovals), there is nothing here a quorum
  // could be set on once the control is disallowed for approvers at all.
  const canShowUngroupedQuorum = computed(
    () =>
      isEnvelope.value &&
      !createStore.ordered &&
      ungroupedParticipants.value.length > 1 &&
      hasSignee(ungroupedParticipants.value)
  );

  // Without a sign order every invitee shares one bucket, so a single approver
  // anywhere in the list makes that bucket mixed — the quorum is then shown as
  // unsupported, exactly like a mixed group in the sequenced view. Reachable
  // because the order switch is never disabled: turning it off while approvers
  // are invited collapses both types into the one bucket.
  const isUngroupedMixed = computed(() =>
    hasApprover(ungroupedParticipants.value)
  );

  return {
    ungroupedParticipants,
    isMixedGroup,
    canShowGroupQuorum,
    canShowUngroupedQuorum,
    isUngroupedMixed
  };
}
